import { useNuxtApp } from "#app"
import { defineStore } from "pinia"
import { AdminApi, Kit, KitTag, Tag } from "@evercam/api"
import { useReportStore } from "@/stores/report"
import { useKitSummaryStore } from "@/stores/kitSummary"

type TagStore = {
  tags: Tag[]
  isLoading: boolean
}

export const useTagStore = defineStore({
  id: "tag",
  state: (): TagStore => ({
    tags: [],
    isLoading: false,
  }),
  getters: {
    reportStore() {
      return useReportStore()
    },
    kitSummaryStore() {
      return useKitSummaryStore()
    },
  },
  actions: {
    async fetchTags(force = false) {
      if (this.tags.length && !force) {
        return
      }
      try {
        this.tags = await AdminApi.tags.index()
      } catch (error) {
        useNuxtApp().nuxt2Context.$notifications.error({
          text: "Failed to fetch tags!",
          error,
        })
      }
    },
    async createTag(tag: Tag) {
      try {
        const created = await AdminApi.tags.create(tag)
        this.tags.push(created)

        return created
      } catch (error) {
        useNuxtApp().nuxt2Context.$notifications.error({
          text: "Failed to create tag!",
          error,
        })
      }
    },
    async updateTag(tag: Tag) {
      try {
        await AdminApi.tags.update(tag)
        this.tags = this.tags.map((t) => (t.id === tag.id ? tag : t))
      } catch (error) {
        useNuxtApp().nuxt2Context.$notifications.error({
          text: "Failed to update tag!",
          error,
        })
      }
    },
    async deleteTag(tag: Tag) {
      try {
        await AdminApi.tags.delete(tag)

        this.tags = this.tags.reduce(
          (acc, t) => (t.id === tag.id ? acc : [...acc, t]),
          []
        )
      } catch (error) {
        useNuxtApp().nuxt2Context.$notifications.error({
          text: "Failed to delete tag!",
          error,
        })
      }
    },
    async toggleKitTag({ tag, kit }: KitTag) {
      if (kit.tags.some((t) => t.id === tag.id)) {
        await this.untagKit({ tag, kit })
      } else {
        await this.tagKit({ tag, kit })
      }
    },
    async tagKit({ tag, kit }: KitTag) {
      try {
        await AdminApi.kits.attachTag(kit.id, tag.id)
        this.maybeUpdateStores({
          ...kit,
          tags: [...kit.tags, tag],
        })
      } catch (error) {
        useNuxtApp().nuxt2Context.$notifications.error({
          text: "Failed to tag kit!",
          error,
        })
      }
    },
    async untagKit({ tag, kit }: KitTag) {
      try {
        await AdminApi.kits.detachTag(kit.id, tag.id)
        this.maybeUpdateStores({
          ...kit,
          tags: kit.tags.filter((t) => t.id !== tag.id),
        })
      } catch (error) {
        useNuxtApp().nuxt2Context.$notifications.error({
          text: "Failed to remove tag from kit!",
          error,
        })
      }
    },
    maybeUpdateStores(kit: Kit) {
      this.maybeUpdateReportStore(kit)
      this.maybeUpdateKitSummaryStore(kit)
    },
    maybeUpdateKitSummaryStore(kit: Kit) {
      if (this.kitSummaryStore.kit?.id !== kit.id) {
        return
      }
      this.kitSummaryStore.kit = kit
    },
    maybeUpdateReportStore(item: Kit) {
      if (!this.reportStore.items?.length) {
        return
      }
      this.reportStore.items = (this.reportStore.items as Kit[]).map((k) =>
        k.id === item.id ? item : k
      )
    },
    matchTag(tag: Tag, search: string): boolean {
      if (!search) {
        return true
      }

      const clean = (str: string) =>
        str.replaceAll("-", "").replaceAll(" ", "").toLowerCase()

      return (clean(tag.name) + clean(tag.description)).includes(clean(search))
    },
  },
})
